import { useState, useCallback, useMemo } from 'react';
import { BlueprintLocation } from '../../types/protocols';
import { Unit, UnitCreateInput, UnitInput } from '../../types/units';
import { unitsApi } from '../../api/unitsApi';
import { roomsApi } from '../../api/roomsApi';
import { sortedUnits as sortUnits, unitsOnLevel as filterUnitsOnLevel } from '../../Helpers/unitUtils';
import { useBuilding } from '../../Helpers/BuildingContext';

interface UseUnitsProps {
    buildingKey: string | null | undefined;
}

type PhotoUploader = (config: { storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }) => Promise<unknown>;

interface UseUnitsReturn {
    units: { [unitKey: string]: Unit };
    /** Every unit: apartments first, then commercial, natural number order. */
    sortedUnits: Unit[];
    /** Units with a room on the level (a multi-level unit is on every one of its levels). */
    unitsOnLevel: (levelKey: string) => Unit[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    createUnit: (input: UnitCreateInput) => Promise<Unit>;
    updateUnit: (unitKey: string, input: UnitInput) => Promise<void>;
    /** Moves one of the unit's rooms on a drawing (a single-room unit dragged as a pin, plan D5). */
    moveUnitRoom: (roomKey: string, imageKey: string, location: BlueprintLocation) => Promise<void>;
    deleteUnit: (unitKey: string) => Promise<void>;
    deleteUnitPhoto: (unitKey: string, photoKey: string) => Promise<void>;
    /**
     * Uploads unit photos: verifies admin, hands the path config to the
     * caller's uploader (usePhotoUpload.uploadPhotos), stamps the unit, refetches.
     */
    uploadUnitPhotos: (unitKey: string, uploader: PhotoUploader) => Promise<void>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

/**
 * Units (lägenheter and lokaler) of the current building. The data comes from
 * the already loaded BuildingContext (`model.units`); BuildingContext has no
 * per-subtree patch, so every mutation refetches the building afterwards
 * (same as useStorageUnits and useParking).
 */
export const useUnits = ({ buildingKey }: UseUnitsProps): UseUnitsReturn => {
    const { currentBuilding, loadingBuilding, fetchBuilding } = useBuilding();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const units = useMemo(() => {
        if (!currentBuilding || currentBuilding.key !== buildingKey) return {};
        return unitsApi.parseUnits(currentBuilding.model.units);
    }, [currentBuilding, buildingKey]);

    const sortedUnits = useMemo(() => sortUnits(units), [units]);

    // A unit is on its rooms' levels (plan D5).
    const rooms = currentBuilding && currentBuilding.key === buildingKey ? currentBuilding.model.structure?.rooms : undefined;
    const unitsOnLevel = useCallback((levelKey: string) => filterUnitsOnLevel(sortedUnits, levelKey, rooms), [sortedUnits, rooms]);

    const refresh = useCallback(async () => {
        if (buildingKey) {
            await fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    const run = useCallback(async <T,>(fallbackMessage: string, action: () => Promise<T>): Promise<T> => {
        if (!buildingKey) {
            const message = 'Ingen byggnad vald';
            setError(message);
            throw new Error(message);
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await refresh();
            return result;
        } catch (err) {
            console.error(fallbackMessage, err);
            setError(err instanceof Error ? err.message : fallbackMessage);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, refresh]);

    const createUnit = useCallback((input: UnitCreateInput) =>
        run('Kunde inte skapa enheten', () => unitsApi.createUnit(buildingKey!, input)),
    [run, buildingKey]);

    const updateUnit = useCallback((unitKey: string, input: UnitInput) =>
        run('Kunde inte uppdatera enheten', () => unitsApi.updateUnit(buildingKey!, unitKey, input)),
    [run, buildingKey]);

    const moveUnitRoom = useCallback((roomKey: string, imageKey: string, location: BlueprintLocation) =>
        run('Kunde inte flytta enheten', () => roomsApi.setRoomLocation(buildingKey!, roomKey, imageKey, location)),
    [run, buildingKey]);

    const deleteUnit = useCallback((unitKey: string) =>
        run('Kunde inte ta bort enheten', () => unitsApi.deleteUnit(buildingKey!, unitKey)),
    [run, buildingKey]);

    const deleteUnitPhoto = useCallback((unitKey: string, photoKey: string) =>
        run('Kunde inte ta bort fotot', () => unitsApi.deleteUnitPhoto(buildingKey!, unitKey, photoKey)),
    [run, buildingKey]);

    const uploadUnitPhotos = useCallback((unitKey: string, uploader: PhotoUploader) =>
        run('Kunde inte ladda upp foton', async () => {
            const config = await unitsApi.prepareUnitPhotoUpload(buildingKey!, unitKey);
            await uploader(config);
            await unitsApi.stampUnitUpdated(buildingKey!, unitKey);
        }),
    [run, buildingKey]);

    const clearError = useCallback(() => setError(null), []);

    return {
        units,
        sortedUnits,
        unitsOnLevel,
        loading: loadingBuilding,
        error,
        submitting,
        createUnit,
        updateUnit,
        moveUnitRoom,
        deleteUnit,
        deleteUnitPhoto,
        uploadUnitPhotos,
        refresh,
        clearError,
    };
};
